/**
 * Context Service - Trip context extraction and prompt enhancement
 *
 * This service extracts and manages trip context information to provide
 * AI models with relevant, up-to-date information about the user's trip.
 *
 * Key Responsibilities:
 * - Extract comprehensive trip context from all packages
 * - Monitor trip data changes for context updates
 * - Generate contextual prompts and information
 * - Maintain context cache for performance
 * - Provide context relevance scoring
 */

import { eventBridgeService } from '@trip-planner-monorepo/shared';

class ContextService {
  constructor() {
    this.isInitialized = false;
    this.contextCache = new Map(); // Cache trip contexts
    this.contextExpiryTime = 5 * 60 * 1000; // 5 minutes cache
    this.dataServices = new Map(); // References to domain package services
  }

  /**
   * Initialize context service
   */
  async init() {
    if (this.isInitialized) {
      console.warn('ContextService already initialized');
      return;
    }

    // Subscribe to data change events
    eventBridgeService.subscribe('TRIP_DATA_UPDATED', this.handleDataUpdate.bind(this));
    eventBridgeService.subscribe('DAY_UPDATED', this.handleDataUpdate.bind(this));
    eventBridgeService.subscribe('ACCOMMODATION_UPDATED', this.handleDataUpdate.bind(this));
    eventBridgeService.subscribe('PLACE_UPDATED', this.handleDataUpdate.bind(this));
    eventBridgeService.subscribe('TRAVEL_UPDATED', this.handleDataUpdate.bind(this));

    // Initialize data service references
    await this.initializeDataServices();

    this.isInitialized = true;
    console.log('ContextService initialized');
  }

  /**
   * Initialize references to domain package services
   */
  async initializeDataServices() {
    // TODO: Import and store references to domain services
    // This will allow direct access to trip data

    // Example structure:
    // const { daysService } = await import('@trip-planner-monorepo/days');
    // const { placesService } = await import('@trip-planner-monorepo/places');
    // const { accommodationService } = await import('@trip-planner-monorepo/accommodation');
    // const { travelService } = await import('@trip-planner-monorepo/travel');

    console.log('Data service references initialized');
  }

  /**
   * Extract comprehensive trip context
   * @param {string} userId - User identifier
   * @param {string} tripId - Trip identifier
   * @returns {Object} Complete trip context
   */
  async extractTripContext(userId, tripId) {
    const cacheKey = `${userId}-${tripId}`;

    // Check cache first
    const cached = this.contextCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.contextExpiryTime) {
      return cached.context;
    }

    // Extract fresh context
    const context = {
      trip: await this.extractTripInfo(tripId),
      timeline: await this.extractTimeline(tripId),
      locations: await this.extractLocations(tripId),
      accommodation: await this.extractAccommodation(tripId),
      transportation: await this.extractTransportation(tripId),
      activities: await this.extractActivities(tripId),
      documents: await this.extractDocuments(tripId),
      budget: await this.extractBudgetInfo(tripId),
      preferences: await this.extractUserPreferences(userId),
      recentChanges: await this.extractRecentChanges(tripId),
      suggestions: await this.extractPendingSuggestions(tripId),
      completionStatus: this.calculateCompletionStatus(tripId)
    };

    // Cache the context
    this.contextCache.set(cacheKey, {
      context,
      timestamp: Date.now()
    });

    return context;
  }

  /**
   * Extract basic trip information
   */
  async extractTripInfo(tripId) {
    // TODO: Get trip data from trip service
    return {
      id: tripId,
      name: 'Sample Trip',
      description: 'A wonderful vacation',
      destination: 'Paris, France',
      startDate: '2024-06-15',
      endDate: '2024-06-22',
      duration: 7,
      participants: 2,
      status: 'planning',
      createdAt: '2024-05-01T10:00:00Z',
      lastModified: '2024-05-15T14:30:00Z'
    };
  }

  /**
   * Extract trip timeline and daily structure
   */
  async extractTimeline(tripId) {
    // TODO: Get days data from days service
    return {
      totalDays: 7,
      plannedDays: 5,
      unplannedDays: 2,
      days: [
        {
          date: '2024-06-15',
          name: 'Arrival Day',
          location: 'Paris',
          activities: 3,
          accommodation: 'Hotel Le Marais',
          status: 'planned'
        },
        {
          date: '2024-06-16',
          name: 'City Tour',
          location: 'Central Paris',
          activities: 5,
          accommodation: 'Hotel Le Marais',
          status: 'planned'
        }
        // ... more days
      ]
    };
  }

  /**
   * Extract location and places information
   */
  async extractLocations(tripId) {
    // TODO: Get places data from places service
    return {
      primaryDestination: 'Paris, France',
      regions: ['Central Paris', 'Montmartre', 'Latin Quarter'],
      places: [
        {
          name: 'Eiffel Tower',
          type: 'attraction',
          status: 'planned',
          day: '2024-06-16'
        },
        {
          name: 'Louvre Museum',
          type: 'museum',
          status: 'planned',
          day: '2024-06-17'
        }
      ],
      unvisitedPlaces: [
        'Notre-Dame Cathedral',
        'Arc de Triomphe'
      ]
    };
  }

  /**
   * Extract accommodation information
   */
  async extractAccommodation(tripId) {
    // TODO: Get accommodation data from accommodation service
    return {
      totalNights: 6,
      bookedNights: 6,
      unbookedNights: 0,
      accommodations: [
        {
          name: 'Hotel Le Marais',
          type: 'hotel',
          checkIn: '2024-06-15',
          checkOut: '2024-06-21',
          nights: 6,
          guests: 2,
          cost: 120,
          currency: 'EUR',
          status: 'booked'
        }
      ]
    };
  }

  /**
   * Extract transportation information
   */
  async extractTransportation(tripId) {
    // TODO: Get travel data from travel service
    return {
      totalSegments: 2,
      bookedSegments: 1,
      unbookedSegments: 1,
      transportation: [
        {
          type: 'flight',
          origin: 'New York',
          destination: 'Paris',
          date: '2024-06-15',
          status: 'booked',
          cost: 650,
          currency: 'USD'
        },
        {
          type: 'flight',
          origin: 'Paris',
          destination: 'New York',
          date: '2024-06-22',
          status: 'not_booked',
          estimatedCost: 700,
          currency: 'USD'
        }
      ]
    };
  }

  /**
   * Extract activities and planned events
   */
  async extractActivities(tripId) {
    // TODO: Get activities from days content blocks
    return {
      totalActivities: 15,
      plannedActivities: 12,
      unplannedActivities: 3,
      categories: {
        'sightseeing': 6,
        'dining': 4,
        'cultural': 3,
        'shopping': 2
      },
      upcomingActivities: [
        {
          name: 'Visit Eiffel Tower',
          date: '2024-06-16',
          time: '14:00',
          duration: 120,
          type: 'sightseeing'
        }
      ]
    };
  }

  /**
   * Extract document information
   */
  async extractDocuments(tripId) {
    // TODO: Get documents data from documents service
    return {
      totalDocuments: 8,
      categories: {
        'bookings': 3,
        'tickets': 2,
        'identification': 2,
        'other': 1
      },
      recentUploads: [
        {
          name: 'Hotel Confirmation.pdf',
          uploadedAt: '2024-05-10T09:00:00Z',
          type: 'booking'
        }
      ]
    };
  }

  /**
   * Extract budget and cost information
   */
  async extractBudgetInfo(tripId) {
    // TODO: Calculate budget from all sources
    return {
      totalBudget: 3000,
      currency: 'USD',
      spent: 1420,
      remaining: 1580,
      categories: {
        'accommodation': 720,
        'transportation': 650,
        'food': 300,
        'activities': 200,
        'other': 50
      },
      recentExpenses: [
        {
          description: 'Hotel Le Marais',
          amount: 720,
          currency: 'EUR',
          date: '2024-05-10'
        }
      ]
    };
  }

  /**
   * Extract user preferences and settings
   */
  async extractUserPreferences(userId) {
    // TODO: Get user preferences from user service
    return {
      budget: 'moderate',
      interests: ['culture', 'food', 'history', 'art'],
      travelStyle: 'relaxed',
      dietaryRestrictions: [],
      accessibility: [],
      languages: ['en', 'fr'],
      communication: {
        notifications: true,
        reminders: true,
        suggestions: true
      }
    };
  }

  /**
   * Extract recent changes and activity
   */
  async extractRecentChanges(tripId) {
    // TODO: Get recent activity from event logs
    return [
      {
        type: 'accommodation_added',
        description: 'Added Hotel Le Marais to accommodation',
        timestamp: '2024-05-15T14:30:00Z',
        entity: 'accommodation'
      },
      {
        type: 'activity_planned',
        description: 'Added Eiffel Tower visit to Day 2',
        timestamp: '2024-05-15T13:15:00Z',
        entity: 'day'
      },
      {
        type: 'document_uploaded',
        description: 'Uploaded hotel confirmation document',
        timestamp: '2024-05-10T09:00:00Z',
        entity: 'document'
      }
    ];
  }

  /**
   * Extract pending suggestions
   */
  async extractPendingSuggestions(tripId) {
    // TODO: Get suggestions from suggestion service
    return [
      {
        id: 'sugg-001',
        type: 'accommodation_suggestion',
        description: 'Consider adding backup accommodation for Day 3',
        priority: 'medium',
        createdAt: '2024-05-14T10:00:00Z'
      },
      {
        id: 'sugg-002',
        type: 'activity_suggestion',
        description: 'Visit Musée d\'Orsay - matches your art interests',
        priority: 'low',
        createdAt: '2024-05-13T15:30:00Z'
      }
    ];
  }

  /**
   * Calculate trip completion status
   */
  calculateCompletionStatus(tripId) {
    // TODO: Calculate actual completion metrics
    return {
      overall: 75,
      accommodation: 100,
      transportation: 50,
      activities: 80,
      documents: 90,
      readiness: 'mostly_ready',
      missingItems: [
        'Return flight booking',
        'Travel insurance',
        'Local transportation plan'
      ]
    };
  }

  /**
   * Generate contextual summary for AI
   * @param {Object} context - Full trip context
   * @param {string} focus - Optional focus area
   * @returns {string} Formatted context summary
   */
  generateContextualSummary(context, focus = null) {
    let summary = `Trip Overview: ${context.trip.name} to ${context.trip.destination}\n`;
    summary += `Duration: ${context.trip.duration} days (${context.trip.startDate} to ${context.trip.endDate})\n`;
    summary += `Participants: ${context.trip.participants}\n\n`;

    // Add focus-specific information
    if (focus) {
      switch (focus.toLowerCase()) {
        case 'accommodation':
          summary += this.formatAccommodationContext(context.accommodation);
          break;
        case 'transportation':
          summary += this.formatTransportationContext(context.transportation);
          break;
        case 'activities':
          summary += this.formatActivitiesContext(context.activities);
          break;
        case 'budget':
          summary += this.formatBudgetContext(context.budget);
          break;
        default:
          summary += this.formatGeneralContext(context);
      }
    } else {
      summary += this.formatGeneralContext(context);
    }

    // Add recent changes if significant
    if (context.recentChanges.length > 0) {
      summary += `\nRecent Changes:\n`;
      context.recentChanges.slice(0, 3).forEach(change => {
        summary += `- ${change.description}\n`;
      });
    }

    return summary;
  }

  /**
   * Format accommodation context
   */
  formatAccommodationContext(accommodation) {
    let text = `Accommodation Status:\n`;
    text += `- ${accommodation.bookedNights}/${accommodation.totalNights} nights booked\n`;

    accommodation.accommodations.forEach(acc => {
      text += `- ${acc.name} (${acc.type}): ${acc.checkIn} to ${acc.checkOut}\n`;
    });

    return text;
  }

  /**
   * Format transportation context
   */
  formatTransportationContext(transportation) {
    let text = `Transportation Status:\n`;
    text += `- ${transportation.bookedSegments}/${transportation.totalSegments} segments booked\n`;

    transportation.transportation.forEach(transport => {
      text += `- ${transport.type}: ${transport.origin} to ${transport.destination} (${transport.status})\n`;
    });

    return text;
  }

  /**
   * Format activities context
   */
  formatActivitiesContext(activities) {
    let text = `Activities Status:\n`;
    text += `- ${activities.plannedActivities}/${activities.totalActivities} activities planned\n`;

    Object.entries(activities.categories).forEach(([category, count]) => {
      text += `- ${category}: ${count} activities\n`;
    });

    return text;
  }

  /**
   * Format budget context
   */
  formatBudgetContext(budget) {
    let text = `Budget Status:\n`;
    text += `- Total: ${budget.totalBudget} ${budget.currency}\n`;
    text += `- Spent: ${budget.spent} ${budget.currency} (${Math.round(budget.spent/budget.totalBudget*100)}%)\n`;
    text += `- Remaining: ${budget.remaining} ${budget.currency}\n`;

    return text;
  }

  /**
   * Format general context summary
   */
  formatGeneralContext(context) {
    let text = `Current Status:\n`;
    text += `- Accommodation: ${context.accommodation.bookedNights}/${context.accommodation.totalNights} nights booked\n`;
    text += `- Transportation: ${context.transportation.bookedSegments}/${context.transportation.totalSegments} segments booked\n`;
    text += `- Activities: ${context.activities.plannedActivities} planned\n`;
    text += `- Budget: ${Math.round(context.budget.spent/context.budget.totalBudget*100)}% spent\n`;
    text += `- Overall completion: ${context.completionStatus.overall}%\n`;

    return text;
  }

  /**
   * Handle data updates to invalidate cache
   */
  handleDataUpdate(event) {
    const { userId, tripId } = event.payload;
    const cacheKey = `${userId}-${tripId}`;

    // Invalidate cached context
    this.contextCache.delete(cacheKey);

    console.log(`Context cache invalidated for trip ${tripId}`);
  }

  /**
   * Get context relevance score for a message
   * @param {string} message - User message
   * @param {Object} context - Trip context
   * @returns {Object} Relevance scores by category
   */
  getContextRelevance(message, context) {
    const messageLower = message.toLowerCase();
    const scores = {
      accommodation: 0,
      transportation: 0,
      activities: 0,
      budget: 0,
      timeline: 0,
      general: 0
    };

    // Accommodation keywords
    const accommodationKeywords = ['hotel', 'stay', 'room', 'booking', 'check-in', 'check-out'];
    scores.accommodation = this.calculateKeywordScore(messageLower, accommodationKeywords);

    // Transportation keywords
    const transportationKeywords = ['flight', 'train', 'bus', 'transport', 'travel', 'airport'];
    scores.transportation = this.calculateKeywordScore(messageLower, transportationKeywords);

    // Activities keywords
    const activitiesKeywords = ['visit', 'see', 'do', 'activity', 'attraction', 'restaurant'];
    scores.activities = this.calculateKeywordScore(messageLower, activitiesKeywords);

    // Budget keywords
    const budgetKeywords = ['cost', 'price', 'budget', 'money', 'expensive', 'cheap'];
    scores.budget = this.calculateKeywordScore(messageLower, budgetKeywords);

    // Timeline keywords
    const timelineKeywords = ['when', 'schedule', 'time', 'day', 'date', 'itinerary'];
    scores.timeline = this.calculateKeywordScore(messageLower, timelineKeywords);

    // General trip keywords
    const generalKeywords = ['trip', 'vacation', 'plan', 'suggest', 'recommend'];
    scores.general = this.calculateKeywordScore(messageLower, generalKeywords);

    return scores;
  }

  /**
   * Calculate keyword relevance score
   */
  calculateKeywordScore(message, keywords) {
    const foundKeywords = keywords.filter(keyword => message.includes(keyword));
    return foundKeywords.length / keywords.length;
  }

  /**
   * Clear context cache
   */
  clearCache() {
    this.contextCache.clear();
    console.log('Context cache cleared');
  }
}

// Create singleton instance
const contextService = new ContextService();

export { contextService };
export default contextService;